<template>
  <section id="scenarios" class="scenarios-section" aria-label="Conditional Scenarios">
    <div class="scenarios-section__inner">
      <div class="section__header">
        <div class="section-number">IV</div>
        <div class="section__titles">
          <p class="eyebrow">Conditional Probability &mdash; The Editor's Sidebar</p>
          <h2 class="display-lg">Change one thing. <em>See the impact.</em></h2>
          <p class="body-lg section__dek">
            This is the power of the system: filter by any condition and instantly see how the
            probabilities shift. A different pitcher line, a stolen base, a quiet bullpen &mdash;
            each moves the needle, and by how much.
          </p>
        </div>
      </div>

      <hr class="rule rule--double" />

      <ol class="scenarios-list">
        <li
          v-for="(scenario, idx) in scenarios"
          :key="idx"
          class="scenario"
          :style="{ '--delay': `${idx * 0.08}s` }"
        >
          <div class="scenario__index">
            <span class="scenario__n numeric">N° {{ pad(idx + 1) }}</span>
          </div>

          <div class="scenario__body">
            <p class="scenario__condition">
              <span class="scenario__if">If</span>
              <em>{{ conditionText(scenario.condition) }}</em>
            </p>

            <div class="scenario__comparison">
              <div class="comparison__side">
                <span class="comparison__label">Baseline</span>
                <span class="comparison__value numeric"
                  >{{ (scenario.baseline_win_pct * 100).toFixed(1) }}%</span
                >
              </div>
              <span class="comparison__arrow" aria-hidden="true">&rarr;</span>
              <div class="comparison__side comparison__side--scenario">
                <span class="comparison__label">Scenario</span>
                <span class="comparison__value numeric"
                  >{{ (scenario.scenario_win_pct * 100).toFixed(1) }}%</span
                >
              </div>
            </div>
          </div>

          <div
            class="scenario__delta"
            :class="{
              'delta--positive': scenario.scenario_win_pct > scenario.baseline_win_pct,
              'delta--negative': scenario.scenario_win_pct < scenario.baseline_win_pct
            }"
          >
            <span class="delta__value numeric">
              {{ deltaValue(scenario) }}
            </span>
            <span class="delta__label">
              {{
                scenario.scenario_win_pct > scenario.baseline_win_pct
                  ? '↑ Boost'
                  : scenario.scenario_win_pct < scenario.baseline_win_pct
                    ? '↓ Decline'
                    : '— Flat'
              }}
            </span>
          </div>
        </li>
      </ol>
    </div>
  </section>
</template>

<script setup lang="ts">
interface Scenario {
  condition: string
  baseline_win_pct: number
  scenario_win_pct: number
}

defineProps<{
  scenarios: Scenario[]
}>()

function pad(n: number): string {
  return String(n).padStart(2, '0')
}

/** Strip a leading "If " from conditions so we can set our own typographic "If". */
function conditionText(condition: string): string {
  return condition.replace(/^if\s+/i, '')
}

function deltaValue(scenario: Scenario): string {
  const delta = (scenario.scenario_win_pct - scenario.baseline_win_pct) * 100
  const sign = delta > 0 ? '+' : ''
  return `${sign}${delta.toFixed(1)}%`
}
</script>

<style scoped>
.scenarios-section {
  max-width: 1280px;
  margin: 0 auto;
  padding: 4rem 2rem;
  border-top: var(--rule-thin) solid var(--ink);
}

.section__header {
  display: grid;
  grid-template-columns: auto 1fr;
  gap: 2rem;
  align-items: start;
  max-width: 960px;
}

.section__titles .eyebrow {
  margin-bottom: 1rem;
}

.section__titles .display-lg em {
  font-style: italic;
  color: var(--ox);
}

.section__dek {
  margin-top: 1.25rem;
  max-width: 58ch;
}

/* ─── Scenarios list (editorial sidebar) ────────────────────────── */
.scenarios-list {
  list-style: none;
  padding: 0;
  margin: 0;
  border-top: var(--rule-thin) solid var(--ink);
}

.scenario {
  display: grid;
  grid-template-columns: auto 1fr auto;
  align-items: center;
  gap: 1.5rem;
  padding: 1.5rem 0.5rem;
  border-bottom: var(--rule-thin) solid var(--paper-edge);
  animation: fadeUp 0.55s ease both;
  animation-delay: var(--delay);
  transition: background 0.15s ease;
}

.scenario:hover {
  background: var(--paper-deep);
}

/* Index column */
.scenario__index {
  min-width: 3.25rem;
}

.scenario__n {
  font-family: var(--font-display);
  font-variation-settings:
    'opsz' 72,
    'wght' 700;
  font-size: 1.5rem;
  font-style: italic;
  color: var(--ox);
}

/* Body column */
.scenario__body {
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
}

.scenario__condition {
  font-family: var(--font-editorial);
  font-size: 1.1rem;
  line-height: 1.4;
  color: var(--ink);
}

.scenario__if {
  font-family: var(--font-display);
  font-variation-settings:
    'opsz' 48,
    'wght' 600;
  font-style: italic;
  color: var(--ox);
  margin-right: 0.35rem;
  font-size: 1.15rem;
}

.scenario__condition em {
  font-style: italic;
  color: var(--ink);
  font-family: var(--font-editorial);
}

.scenario__comparison {
  display: inline-flex;
  align-items: center;
  gap: 1.25rem;
  padding: 0.5rem 0.85rem;
  border: var(--rule-thin) solid var(--ink);
  background: var(--paper);
  align-self: flex-start;
}

.comparison__side {
  display: flex;
  flex-direction: column;
  gap: 0.15rem;
  min-width: 4rem;
}

.comparison__label {
  font-family: var(--font-ui);
  font-size: 0.58rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.2em;
  color: var(--ink-faint);
}

.comparison__value {
  font-family: var(--font-data);
  font-size: 1.1rem;
  font-weight: 700;
  color: var(--ink);
}

.comparison__side--scenario .comparison__value {
  color: var(--ink);
}

.comparison__arrow {
  font-family: var(--font-display);
  font-size: 1.1rem;
  color: var(--ox);
  font-style: italic;
}

/* Delta column */
.scenario__delta {
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  gap: 0.2rem;
  min-width: 7rem;
  padding-left: 1rem;
  border-left: var(--rule-thick) solid var(--ink);
  text-align: right;
}

.delta__value {
  font-family: var(--font-display);
  font-variation-settings:
    'opsz' 72,
    'wght' 700;
  font-size: 1.75rem;
  line-height: 1;
  color: var(--ink);
  font-variant-numeric: tabular-nums;
}

.delta__label {
  font-family: var(--font-ui);
  font-size: 0.6rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.2em;
  color: var(--ink-faint);
}

.delta--positive {
  border-left-color: var(--ivy);
}

.delta--positive .delta__value,
.delta--positive .delta__label {
  color: var(--ivy);
}

.delta--negative {
  border-left-color: var(--bruise);
}

.delta--negative .delta__value,
.delta--negative .delta__label {
  color: var(--bruise);
}

@keyframes fadeUp {
  from {
    opacity: 0;
    transform: translateY(12px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

@media (max-width: 900px) {
  .scenario {
    grid-template-columns: auto 1fr;
    gap: 1rem 1.25rem;
  }

  .scenario__delta {
    grid-column: 1 / -1;
    flex-direction: row;
    justify-content: space-between;
    align-items: baseline;
    border-left: none;
    border-top: var(--rule-thick) solid var(--ink);
    padding-left: 0;
    padding-top: 0.75rem;
    text-align: left;
  }

  .delta--positive {
    border-top-color: var(--ivy);
  }

  .delta--negative {
    border-top-color: var(--bruise);
  }
}

@media (max-width: 700px) {
  .scenarios-section {
    padding: 3rem 1rem;
  }

  .section__header {
    grid-template-columns: 1fr;
    gap: 1rem;
  }

  .scenario__comparison {
    flex-wrap: wrap;
    gap: 0.75rem;
  }
}
</style>
